import React, { useEffect, useRef, useState } from 'react';
import * as atlas from 'azure-maps-control';
import { useAppSettings } from '~/AppSettingsContext';
import type { RouteWaypoint } from './useAzureMapsRoute';

// Import Azure Maps CSS
import 'azure-maps-control/dist/atlas.min.css';

export interface AzureMapComponentProps {
	waypoints: RouteWaypoint[];
	routeGeometry?: [number, number][];
	startingPoint?: RouteWaypoint | null;
	className?: string;
}

export function AzureMapComponent({ waypoints, routeGeometry, startingPoint, className }: AzureMapComponentProps) {
	const { atlasSubscriptionKey } = useAppSettings();
	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<atlas.Map | null>(null);
	const [mapReady, setMapReady] = useState(false);

	// Initialize map
	useEffect(() => {
		if (!mapContainerRef.current || mapRef.current) return;

		const map = new atlas.Map(mapContainerRef.current, {
			authOptions: {
				authType: atlas.AuthenticationType.subscriptionKey,
				subscriptionKey: atlasSubscriptionKey
			},
			center: [-98.5795, 39.8283], // Center of US
			zoom: 4,
			style: 'road'
		});

		map.events.add('ready', () => {
			setMapReady(true);
		});

		mapRef.current = map;

		return () => {
			map.dispose();
			mapRef.current = null;
			setMapReady(false);
		};
	}, [atlasSubscriptionKey]);

	// Update map markers and route
	useEffect(() => {
		if (!mapReady || !mapRef.current) return;

		const map = mapRef.current;

		// Clear existing layers and sources
		const existingLayers = map.layers.getLayers();
		existingLayers.forEach(layer => {
			if (layer.getId()?.startsWith('route-') || layer.getId()?.startsWith('marker-')) {
				map.layers.remove(layer);
			}
		});

		// Remove existing data sources
		try {
			map.sources.remove('routeSource');
		} catch {
			// Source doesn't exist, ignore
		}
		try {
			map.sources.remove('waypointsSource');
		} catch {
			// Source doesn't exist, ignore
		}

		// Combine all points (starting point + waypoints)
		const allPoints: RouteWaypoint[] = [];
		if (startingPoint) {
			allPoints.push(startingPoint);
		}
		allPoints.push(...waypoints);

		if (allPoints.length === 0) return;

		// Create waypoints data source
		const waypointsSource = new atlas.source.DataSource('waypointsSource');
		map.sources.add(waypointsSource);

		// Add markers for each waypoint
		allPoints.forEach((wp, index) => {
			const isStart = startingPoint && index === 0;
			const displayIndex = startingPoint ? index : index + 1;

			const point = new atlas.data.Feature(new atlas.data.Point([wp.longitude, wp.latitude]), {
				label: isStart ? 'S' : displayIndex.toString(),
				title: wp.label || wp.address || `Stop ${displayIndex}`,
				isStart
			});
			waypointsSource.add(point);
		});

		// Add symbol layer for markers
		const markerLayer = new atlas.layer.SymbolLayer(waypointsSource, 'marker-layer', {
			iconOptions: {
				image: 'marker-blue',
				allowOverlap: true,
				anchor: 'bottom'
			},
			textOptions: {
				textField: ['get', 'label'],
				offset: [0, -2.5],
				color: '#fff',
				size: 12,
				font: ['StandardFont-Bold']
			}
		});

		map.layers.add(markerLayer);

		// Add route line if geometry is provided
		if (routeGeometry && routeGeometry.length > 0) {
			const routeSource = new atlas.source.DataSource('routeSource');
			map.sources.add(routeSource);

			const routeLine = new atlas.data.Feature(new atlas.data.LineString(routeGeometry));
			routeSource.add(routeLine);

			const routeLayer = new atlas.layer.LineLayer(routeSource, 'route-line', {
				strokeColor: '#2563eb',
				strokeWidth: 4,
				lineJoin: 'round',
				lineCap: 'round'
			});

			// Add route layer below markers
			map.layers.add(routeLayer, 'marker-layer');
		}

		// Fit map bounds to show all points
		if (allPoints.length > 0) {
			const positions = allPoints.map(wp => new atlas.data.Position(wp.longitude, wp.latitude));
			const bounds = atlas.data.BoundingBox.fromPositions(positions);

			map.setCamera({
				bounds,
				padding: 50
			});
		}
	}, [mapReady, waypoints, routeGeometry, startingPoint]);

	return (
		<div ref={mapContainerRef} className={`size-full min-h-[400px] rounded-lg ${className || ''}`} />
	);
}
